/**
 * ProjectHealthCard Component
 * AIDLC Administrative Backend
 *
 * Health summary card for the cross-project overview cockpit.
 * Shows project name, AIDLC state, sprint info, health indicator, agent/item counts.
 */

import { Badge } from '../../shared/Badge';
import type { ProjectOverview } from '../../../types/projects';

interface ProjectHealthCardProps {
  project: ProjectOverview;
  onClick?: () => void;
}

const healthDotColors: Record<string, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
};

const healthLabels: Record<string, string> = {
  green: 'Healthy',
  yellow: 'At Risk',
  red: 'Critical',
};

const stateVariants: Record<string, 'info' | 'warning' | 'success' | 'neutral' | 'default' | 'purple'> = {
  intent: 'neutral',
  requirements: 'default',
  planning: 'info',
  estimating: 'warning',
  implementing: 'info',
  delivering: 'purple',
  complete: 'success',
};

export function ProjectHealthCard({ project, onClick }: ProjectHealthCardProps) {
  return (
    <div
      className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer dark:bg-zinc-900 dark:border-zinc-800"
      onClick={onClick}
    >
      {/* Header: name + health */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{project.name}</h3>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`w-2.5 h-2.5 rounded-full ${healthDotColors[project.health] || 'bg-gray-400'}`} />
          <span className="text-[10px] text-gray-500">{healthLabels[project.health] || project.health}</span>
        </div>
      </div>

      {/* State badge */}
      <div className="mb-3">
        <Badge variant={stateVariants[project.aidlcState] || 'neutral'}>
          {project.aidlcState.charAt(0).toUpperCase() + project.aidlcState.slice(1)}
        </Badge>
      </div>

      {/* Sprint info */}
      <div className="text-xs text-gray-600 mb-3">
        {project.activeSprintName ? (
          <span><span className="font-medium text-ldaja-blue">{project.activeSprintName}</span> Active</span>
        ) : (
          <span className="italic text-gray-400">No Active Sprint</span>
        )}
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 text-[10px] text-gray-500">
        <span>Agents: <span className="font-medium text-gray-700">{project.agentCount}</span></span>
        <span>Items: <span className="font-medium text-gray-700">{project.backlogItemCount}</span></span>
        <span>
          Blockers:{' '}
          <span className={`font-medium ${project.blockerCount > 0 ? 'text-red-600' : 'text-gray-700'}`}>
            {project.blockerCount}
          </span>
        </span>
      </div>
    </div>
  );
}
